import { useCallback, useMemo, useState } from "react";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { isDeletableInvoice } from "@common/helpers/invoice-status-machine";
import { Invoice } from "@common/types/invoice";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { useBulkDeleteInvoices } from "@api/invoice.api";

interface UseInvoiceBulkDeleteParams {
    selectedInvoices: Invoice[];
    onActionComplete?: () => void;
}

export function useInvoiceBulkDelete({
    selectedInvoices,
    onActionComplete,
}: UseInvoiceBulkDeleteParams) {
    const { workspaceId } = useScope();
    const { toast } = useToaster();
    const bulkDelete = useBulkDeleteInvoices();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const deletableInvoices = useMemo(
        () => selectedInvoices.filter(isDeletableInvoice),
        [selectedInvoices],
    );
    const skippedCount = selectedInvoices.length - deletableInvoices.length;

    const openBulkDeleteModal = useCallback(() => {
        setIsConfirmOpen(true);
    }, []);

    const closeBulkDeleteModal = useCallback(() => {
        setIsConfirmOpen(false);
    }, []);

    const confirmBulkDelete = useCallback(async () => {
        if (!workspaceId || deletableInvoices.length === 0) {
            setIsConfirmOpen(false);
            return;
        }
        try {
            const { succeeded, failed } = await bulkDelete.mutateAsync({
                workspaceId,
                invoiceIds: deletableInvoices.map((invoice) => invoice.id),
            });
            if (failed.length === 0) {
                toast(
                    `Deleted ${pluralizeWithCount("invoice", succeeded.length)}`,
                    undefined,
                    { variant: "success" },
                );
            } else if (succeeded.length === 0) {
                toast(
                    `Failed to delete ${pluralize("invoice", failed.length)}: ${failed[0].message}`,
                    undefined,
                    { variant: "error" },
                );
            } else {
                toast(
                    `Deleted ${pluralizeWithCount("invoice", succeeded.length)}, ${failed.length} failed: ${failed[0].message}`,
                    undefined,
                    { variant: "warning" },
                );
            }
            onActionComplete?.();
        } catch {
            toast("Failed to delete invoices", undefined, { variant: "error" });
        } finally {
            setIsConfirmOpen(false);
        }
    }, [workspaceId, deletableInvoices, bulkDelete, toast, onActionComplete]);

    return {
        isBulkDeleteConfirmOpen: isConfirmOpen,
        deletableCount: deletableInvoices.length,
        skippedCount,
        canBulkDelete: deletableInvoices.length > 0,
        openBulkDeleteModal,
        closeBulkDeleteModal,
        confirmBulkDelete,
        isProcessingBulkDelete: bulkDelete.isPending,
    };
}
